import { useState } from 'react';
import {
  IconIfc, IconPdf, IconDwg, IconRaum, IconProjekt, IconLage,
  IconWarnung, IconOk,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';

/**
 * Was in einem Revit-Modell eingerichtet sein muss, damit der Export läuft.
 *
 * Die Seite steht hier und nicht nur in einem Dokument, weil sie gebraucht
 * wird, während jemand vor Revit sitzt - und weil sich manches davon erst im
 * Betrieb herausgestellt hat.
 */
export function Modellvorgaben() {
  const [offen, setOffen] = useState<string | null>('grundlagen');

  const umschalten = (name: string) => setOffen(offen === name ? null : name);

  return (
    <>
      <PageHeader
        titel="Was im Modell eingerichtet sein muss"
        beschreibung={'Die Plattform exportiert, was das Modell hergibt. Fehlt eine '
          + 'Einstellung, bricht der Export ab oder liefert etwas anderes als erwartet. '
          + 'Das meiste davon gehört in die Modellvorlage und gilt dann für alle neuen '
          + 'Projekte.'}
        meta="Gilt für Revit 2025"
      />

      <Abschnitt
        name="grundlagen"
        offen={offen === 'grundlagen'}
        umschalten={umschalten}
        icon={<IconProjekt />}
        titel="Einmalig je Modell"
        kurz="Projektnummer, Standort, gemeinsame Parameter"
      >
        <Punkt titel="Projektnummer">
          <em>Verwalten → Projektinformationen → Projektnummer</em>
          <p>
            Über sie ordnet das Add-in das Modell der Plattform zu, und sie
            erscheint in den Dateinamen. Fehlt sie, läuft die Zuordnung nur über
            die ACC-Kennung.
          </p>
        </Punkt>

        <Punkt titel="Projektstandort">
          <em>Verwalten → Standort</em>
          <p>
            Ohne Angabe stehen dort die Revit-Vorgabewerte für Massachusetts. Im
            IFC landen sie als geografische Lage — für ein Münchner Projekt
            offensichtlich falsch, aber es fällt erst beim Empfänger auf.
          </p>
        </Punkt>

        <Punkt titel="Modell bei der Plattform anmelden">
          <em>Zusatzmodule → TuV → Modell anmelden</em>
          <p>
            Einmal je Modell. Die ACC-Kennungen werden dabei aus dem geöffneten
            Modell gelesen — in der ACC-Oberfläche sind sie nicht sichtbar.
          </p>
        </Punkt>
      </Abschnitt>

      <Abschnitt
        name="ifc"
        offen={offen === 'ifc'}
        umschalten={umschalten}
        icon={<IconIfc />}
        titel="IFC-Export"
        kurz="Exporteinstellung, Koordinatenbasis, Exportansicht"
      >
        <Punkt titel="Exporteinstellung anlegen">
          <em>Datei → Exportieren → IFC → Einrichtung ändern</em>
          <p>
            Eine Einstellung mit dem Namen <code>IFC_EXPORT_TGA</code> anlegen und
            speichern. Sie wird im Modell abgelegt und gilt dort dauerhaft.
          </p>
        </Punkt>

        <Punkt titel="Koordinatenbasis" wichtig>
          <em>Reiter „Geografische Referenz" → Projektursprung</em>
          <p>
            Auf <strong>Basispunkt des Projektes</strong> stellen. Damit liegen
            die Fachmodelle im selben lokalen System und passen beim
            Zusammenführen zueinander.
          </p>
          <p>
            Die Einstellung steht <strong>nicht</strong> im Reiter „Erweitert",
            wo man sie zuerst sucht.
          </p>
        </Punkt>

        <Punkt titel="EPSG-Code eintragen">
          <em>Geografische Referenz → EPSG Code</em>
          <p>
            Für deutsche Projekte meist <code>EPSG:25832</code> (ETRS89 / UTM
            Zone 32N). Damit schreibt Revit eine Georeferenzierung in die Datei,
            und das Modell lässt sich verorten, ohne dass die
            Bauteilkoordinaten aufgebläht werden.
          </p>
          <p>
            Ohne den Code bleiben die Werte in der Einstellung stehen und landen
            nicht im Export.
          </p>
        </Punkt>

        <Punkt titel="Exportansicht">
          <em>Eine 3D-Ansicht namens <code>IFC_EXPORT_TGA</code></em>
          <p>
            Ihre Sichtbarkeitseinstellungen bestimmen, was exportiert wird. Fehlt
            die Ansicht, bricht der Export ab — mit einer Meldung, die die
            vorhandenen auflistet.
          </p>
        </Punkt>

        <Hinweis>
          Die im Modell gespeicherte Einstellung lässt sich über die
          Programmschnittstelle nicht auslesen. Stimmt die Lage im Ergebnis
          trotzdem nicht, setzt die Plattform sie nachträglich — einzustellen
          unter <em>Projekte → Lage der IFC-Dateien</em>.
        </Hinweis>
      </Abschnitt>

      <Abschnitt
        name="pdf"
        offen={offen === 'pdf'}
        umschalten={umschalten}
        icon={<IconPdf />}
        titel="PDF-Export"
        kurz="Blattsatz TUV_PDF_Export"
      >
        <Punkt titel="Blattsatz anlegen" wichtig>
          <em>Ansicht → Benutzeroberfläche → Projektbrowser → Blattsätze</em>
          <p>
            Einen Satz mit dem Namen <code>TUV_PDF_Export</code> anlegen und die
            zu exportierenden Pläne hineinnehmen.
          </p>
          <p>
            Der Name ist verbindlich — der Exporter sucht genau danach. Fehlt der
            Satz, bricht der Auftrag ab.
          </p>
        </Punkt>

        <Hinweis art="gut">
          Weil der Blattsatz im Modell liegt, bleibt das Exportprofil über alle
          Projekte gleich. Ändert sich der Umfang einer Übergabe, passt die
          Planung den Satz an — niemand muss ein Profil bearbeiten oder eine
          neue Version freigeben.
        </Hinweis>
      </Abschnitt>

      <Abschnitt
        name="dwg"
        offen={offen === 'dwg'}
        umschalten={umschalten}
        icon={<IconDwg />}
        titel="DWG-Export"
        kurz="Exporteinrichtung TUV_DWG_Export, Blattsatz TUV_PDF_Export"
      >
        <Punkt titel="Blattsatz anlegen">
          <p>
            Hier gibt es <strong>zwei</strong> Namen, und sie bezeichnen Verschiedenes.
            Im DWG-Dialog von Revit stehen sie untereinander:
            <br /><br />
            <em>Exporteinrichtung wählen</em> — <code>TUV_DWG_Export</code>. Sie trägt
            die Layerzuordnung, das Dateiformat und die Einheiten.
            <br />
            <em>Ansichten und Pläne: Exportieren</em> — <code>TUV_PDF_Export</code>.
            Das ist der Blattsatz, also welche Pläne exportiert werden. Es gibt nur
            einen, und PDF wie DWG nutzen ihn.
            <br /><br />
            Im Profil dürfen <strong>beide</strong> Namen stehen, durch
            Senkrechtstrich getrennt:{' '}
            <code>TUV_DWG_Export|TUV_PDF_Export</code>. Der erste gefundene gewinnt.
            So läuft der Auftrag, ob Sie einen eigenen DWG-Blattsatz führen oder
            nicht — und das Protokoll nennt, welcher genommen wurde.</p>
        </Punkt>

        <Punkt titel="Layerzuordnung">
          <p>
            Ohne hinterlegte Zuordnungsdatei gelten die Revit-Standardlayer. Das
            ist kein Fehler, wird aber im Auftragsprotokoll vermerkt.
          </p>
          <p>
            Eine eigene Datei lässt sich unter <em>Projekte →
            Konfigurationsdateien</em> hochladen und im Profil zuordnen.
          </p>
        </Punkt>
      </Abschnitt>

      <Abschnitt
        name="raumbuch"
        offen={offen === 'raumbuch'}
        umschalten={umschalten}
        icon={<IconRaum />}
        titel="Raumbuch"
        kurz="MEP-Räume, verlinktes Architekturmodell"
      >
        <Punkt titel="MEP-Räume platzieren" wichtig>
          <em>Analysieren → Raum und Fläche → Räume automatisch platzieren</em>
          <p>
            Das Raumbuch liest MEP-Räume, nicht die Räume des Architekturmodells.
            Letztere gehören dem Architekten und lassen sich von der TGA nicht
            beschreiben — die gebäudetechnischen Werte gehören ohnehin an den
            MEP-Raum.
          </p>
        </Punkt>

        <Punkt titel="Architekturmodell laden" wichtig>
          <p>
            Zu jedem MEP-Raum wird der zugehörige Architekturraum gesucht, indem
            sein Mittelpunkt im verlinkten Modell nachgeschlagen wird. Ist die
            Verlinkung nicht geladen, bleibt die Zuordnung leer.
          </p>
          <p>
            Folge: Wird ein Raum im Architekturmodell gelöscht und neu angelegt,
            erkennt die Plattform das nicht — und die gepflegten Werte lassen sich
            nicht übertragen.
          </p>
        </Punkt>

        <Punkt titel="Parameter für das Rückschreiben">
          <p>
            Zurückgeschrieben wird nur in Parameter, die im Raum bereits
            vorhanden sind. Neue anzulegen wäre ein Eingriff in die
            Projektvorlage und geschieht nicht automatisch — fehlende werden im
            Protokoll genannt.
          </p>
        </Punkt>
      </Abschnitt>

      <Abschnitt
        name="grunddaten"
        offen={offen === 'grunddaten'}
        umschalten={umschalten}
        icon={<IconProjekt />}
        titel="Projektgrunddaten"
        kurz="Bauherr, Beteiligte, Bearbeiter"
      >
        <Punkt titel="Wo sie stehen sollen" wichtig>
          <em>Verwalten → Projektinformationen</em>
          <p>
            Bauherr, Beteiligte, Bearbeiter und Projektangaben gehören in die
            Projektinformation, nicht an jedes Blatt. Die Titelblockfamilien tragen
            dann Beschriftungen mit Verweis darauf — eine Änderung wirkt sofort auf
            alle Pläne.
          </p>
          <p>
            Im Bestand stehen sie an jedem Blatt. In <code>TGAA_001</code> sind das
            56 Stellen je Feld; über neun Modelle einige Hundert. Wechselt ein
            Planungsbüro, muss jede angefasst werden.
          </p>
        </Punkt>

        <Punkt titel="Wie die Plattform schreibt">
          <p>
            Zuerst in die Projektinformation. Fehlt der Parameter dort, an jedes
            Blatt, das ihn trägt. Das Ergebnis nennt je Feld, welcher Weg genommen
            wurde — steht dort „Blätter", ist die Titelblockfamilie noch nicht
            nachgezogen.
          </p>
          <p>
            So funktioniert es heute und wird besser, sobald die Familien Verweise
            tragen. Ohne Änderung an der Plattform.
          </p>
        </Punkt>

        <Punkt titel="Was die Analyse ergab">
          <p>
            Aus der Planliste von <code>TGAA_001</code>: 61 Spalten, davon 23 als
            Projektgrunddaten geführt. Drei Befunde gehören berichtigt.
          </p>
          <p>
            <strong><code>Client Fax</code> enthält eine E-Mail-Adresse.</strong>{' '}
            Entweder ist der Parameter falsch benannt oder falsch belegt.
          </p>
          <p>
            <strong>Für den Bauherrennamen gibt es kein Feld.</strong> Nur Ort,
            Straße, Telefon, Web. Die Plattform führt ihn, kann ihn aber nicht
            schreiben — in den Familien fehlt die Stelle.
          </p>
          <p>
            <strong>Vier <code>Henn</code>-Felder</strong> aus der Vorlage eines
            anderen Büros, leer im Bestand. Vermutlich zu entfernen.
          </p>
        </Punkt>

        <Punkt titel="Wer führt: fixiert oder bearbeitbar" wichtig>
          <p>
            <strong>Bearbeitbar</strong> heißt: Das Modell gewinnt. Ein Auslesen
            übernimmt den Wert. So kommt der Bestand überhaupt herein.
          </p>
          <p>
            <strong>Fixiert</strong> heißt: Die Plattform gewinnt. Ein Auslesen
            vermerkt den Wert aus dem Modell, überschreibt ihn aber nicht — beim
            nächsten Übertragen wird das Modell angeglichen.
          </p>
          <p>
            Bei neun Modellen ist das der einzige Schutz. Ohne Fixierung entscheidet,
            welches Modell zuletzt ausgelesen wurde — Zufall statt Pflege. Ein Wert,
            den Sie von Hand setzen, wird deshalb automatisch fixiert.
          </p>
          <p>
            Der Schalter steht je Feld unter <em>Einstellungen</em>, dazu „Alle
            fixieren" und „Alle freigeben".
          </p>
        </Punkt>
      </Abschnitt>

      <Abschnitt
        name="planstaende"
        offen={offen === 'planstaende'}
        umschalten={umschalten}
        icon={<IconProjekt />}
        titel="Planstände und Indexe"
        kurz="Modellschlüssel, Startansicht, Nummerierung"
      >
        <Punkt titel="Modellschlüssel setzen" wichtig>
          <em>Revit → TuV TGA-Planen 5.0 → Modellvorgaben</em>
          <p>
            Der Knopf legt den gemeinsam genutzten Parameter{' '}
            <code>TuV_Modellschluessel</code> an der Projektinformation an, holt den
            Schlüssel von der Plattform und schreibt ihn. Danach erkennt jedes
            Add-in, in welchem Modell es steckt.
          </p>
          <p>
            Die Kennung des Parameters ist fest:{' '}
            <code>8f3c1d72-6a54-4e19-b03f-2c7d95e14a86</code>. Sie muss über alle
            Modelle gleich sein — legt jedes Modell den Parameter mit eigener an,
            ist derselbe Name in zwei Modellen nicht derselbe Parameter.
          </p>
          <p>
            Die büroweite Freigabedatei wird dabei nicht angefasst. Das Add-in legt
            eine vorübergehende an, bindet den Parameter und hängt die bisherige
            zurück.
          </p>
        </Punkt>

        <Punkt titel="Startansicht anlegen" wichtig>
          <em>Ein Plan mit dem Namen TUV_TGA_XXX_Startansicht</em>
          <p>
            Das <code>XXX</code> steht für das Gewerkskürzel. Die Ansicht muss
            zusätzlich als Startansicht zugewiesen sein — unter{' '}
            <em>Verwalten → Startansicht</em>. Eine richtig benannte Ansicht, die
            niemand sieht, hilft nicht.
          </p>
          <p>
            <strong>Ein Plan, keine Zeichenansicht.</strong> Nur auf einem Plan
            können Bauteillisten liegen, und die Indexliste ist eine — deshalb
            braucht sie keine Übertragung, sie erscheint dort von selbst.
          </p>
          <p>
            Angelegt wird sie nicht automatisch. Wo sie liegt und was darauf steht,
            entscheidet die Planung; das Add-in sagt nur, dass sie fehlt.
          </p>
        </Punkt>

        <Punkt titel="Revisionen je Projekt nummerieren" wichtig>
          <em>Verwalten → Weitere Einstellungen → Änderungen</em>
          <p>
            Die Nummerierung muss auf <strong>je Projekt</strong> stehen. Bei „je
            Plan" trägt jedes Blatt einen eigenen Stand, und ein gemeinsamer Index
            für den Plansatz ist nicht möglich.
          </p>
          <p>
            Revit verweigert dann auch das Lesen der Revisionsnummer. Ein Leselauf
            über <code>ELEA_001</code> ist daran gescheitert, während er über{' '}
            <code>RLTA_001</code> durchlief — dort steht die Nummerierung auf je
            Projekt.
          </p>
        </Punkt>

        <Punkt titel="Ausgegebene Revisionen">
          <p>
            Eine ausgegebene Revision ist in Revit gesperrt: Datum, Beschreibung und
            Ersteller lassen sich nicht ändern. Die Plattform hebt die Sperre beim
            Schreiben kurz auf und setzt sie zurück — am Modell nachgewiesen.
          </p>
          <p>
            Der Index selbst bleibt unveränderlich. Er wird aus der
            Nummerierungsfolge abgeleitet; die Plattform bestimmt nur die
            Reihenfolge.
          </p>
        </Punkt>

        <Punkt titel="Titelblöcke vorbereiten">
          <p>
            Die projektkonstanten Plankopffelder — Architektur, Bauakustik,
            Brandschutz, Bauherrenanschrift und weitere — sollen aus der
            Projektinformation kommen statt an jedem Blatt zu stehen. Dafür muss
            jede verwendete Titelblockfamilie Beschriftungen mit Verweis darauf
            tragen.
          </p>
          <p>
            Im Modell <code>RLTA_001</code> sind fünf Familien im Einsatz; die
            LP3-Familie ist bei keinem Plan verwendet. Welche vorkommen, zeigt die
            Seite <em>Planstände</em> je Modell.
          </p>
        </Punkt>

        <Punkt titel="Datum und Freigabestatus">
          <p>
            Datum einheitlich <code>TT.MM.JJJJ</code>. Im Bestand fanden sich drei
            Formate in einem Modell — <code>03/12/26</code>,{' '}
            <code>13/11/2025</code>, <code>29.05.26</code>. Die Plattform nimmt neun
            Eingabeformen an und schreibt eine.
          </p>
          <p>
            Freigabestatus: <code>P</code> zur Prüfung · <code>V</code> Vorabzug ·{' '}
            <code>F</code> Freigegeben.
          </p>
        </Punkt>
      </Abschnitt>

      <Abschnitt
        name="vorjedem"
        offen={offen === 'vorjedem'}
        umschalten={umschalten}
        icon={<IconWarnung />}
        titel="Vor jedem Export"
        kurz="Verlinkungen laden, synchronisieren"
      >
        <Punkt titel="Verlinkte Modelle laden" wichtig>
          <em>Verwalten → Verknüpfungen verwalten</em>
          <p>
            Exportiert wird, was geladen ist. Ausgeladene Verlinkungen erscheinen
            nicht in Plänen und Schnitten — die Zeichnungen sind dann
            unvollständig, ohne dass ein Fehler gemeldet wird.
          </p>
          <p>
            Der Exporter warnt und listet die fehlenden im Protokoll auf. Beheben
            kann er es nicht.
          </p>
        </Punkt>

        <Punkt titel="Synchronisieren" wichtig>
          <p>
            Exportiert wird der zuletzt synchronisierte Stand aus der Cloud, nicht
            die lokale Fassung. Nicht synchronisierte Änderungen fehlen im
            Ergebnis.
          </p>
          <p>
            Das gilt auch für Einstellungen: Eine neu angelegte
            IFC-Exporteinstellung wirkt erst nach dem Synchronisieren.
          </p>
        </Punkt>
      </Abschnitt>

      <div className="karte">
        <h2 className="karte-titel"><IconLage />Wenn etwas nicht stimmt</h2>
        <p style={{ marginTop: 0, maxWidth: 720 }}>
          Jeder Auftrag führt ein Protokoll — unter <em>Aufträge</em> beim
          jeweiligen Eintrag. Dort steht, welche Einstellungen galten, welche
          Verlinkungen geladen waren und woran es gegebenenfalls lag.
        </p>
        <p className="sehr-schwach" style={{ marginBottom: 0 }}>
          Warnungen brechen den Export nicht ab, sondern weisen auf etwas hin, das
          später auffallen könnte. Sie lohnen einen Blick.
        </p>
      </div>
    </>
  );
}

// --- Bausteine ---------------------------------------------------------------

function Abschnitt({
  name, offen, umschalten, icon, titel, kurz, children,
}: {
  name: string;
  offen: boolean;
  umschalten: (n: string) => void;
  icon: React.ReactNode;
  titel: string;
  kurz: string;
  children: React.ReactNode;
}) {
  return (
    <div className="karte" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => umschalten(name)}
        style={{
          width: '100%', textAlign: 'left', border: 'none', borderRadius: 0,
          padding: '16px 20px', background: offen ? 'var(--flaeche)' : 'transparent',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <span style={{ color: 'var(--akzent)' }}>{icon}</span>
        <span>
          <strong style={{ fontSize: 15 }}>{titel}</strong>
          <br />
          <span className="sehr-schwach">{kurz}</span>
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--text-sehr-schwach)' }}>
          {offen ? '▾' : '▸'}
        </span>
      </button>

      {offen && (
        <div style={{ padding: '4px 20px 18px', borderTop: '1px solid var(--linie)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Punkt({
  titel, wichtig, children,
}: {
  titel: string;
  wichtig?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      marginTop: 16,
      paddingLeft: 14,
      borderLeft: `3px solid ${wichtig ? 'var(--warnung)' : 'var(--linie)'}`,
      maxWidth: 720,
    }}>
      <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>{titel}</h3>
      <div style={{ fontSize: 13, color: 'var(--text-schwach)', lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  );
}

function Hinweis({ children, art = 'warnung' }: { children: React.ReactNode; art?: string }) {
  return (
    <div className={`hinweis ${art === 'gut' ? 'gut' : 'warnung'}`}
         style={{ marginTop: 18, maxWidth: 720 }}>
      {art === 'gut' ? <IconOk size={17} /> : <IconWarnung size={17} />}
      <div>{children}</div>
    </div>
  );
}
